import { useState, useRef, useCallback, useEffect } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import type { ChallengeType, Difficulty } from '../utils/mathProblems';
import type { BallState } from '../components/game/TennisFirstPerson';

export type RoomStatus = 'idle' | 'creating' | 'joining' | 'waiting' | 'playing' | 'error';

export interface RoomInfo {
  id: string;
  room_code: string;
  host_id: string;
  host_name: string;
  guest_id: string | null;
  guest_name: string | null;
  status: 'waiting' | 'playing' | 'finished';
  game_type: ChallengeType;
  difficulty: Difficulty;
  host_score: Record<string, unknown>;
  guest_score: Record<string, unknown>;
  current_question: unknown;
}

export type GameBroadcast =
  | { event: 'round_start'; question: unknown; round: number }
  | { event: 'answer'; playerId: string; answer: string; correct: boolean }
  | { event: 'rally_start'; serverId: string }
  | { event: 'ball'; ball: BallState }
  | { event: 'paddle'; x: number }
  | { event: 'rally_end'; winner: 'host' | 'guest' }
  | { event: 'round_skip' }
  | { event: 'next_round' }
  | { event: 'opponent_ready' };

function getOrCreatePlayerId(): string {
  let id = localStorage.getItem('mt_pid');
  if (!id) {
    id = `p_${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem('mt_pid', id);
  }
  return id;
}

function generateCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export function useOnlineRoom() {
  const [status, setStatus] = useState<RoomStatus>('idle');
  const [room, setRoom] = useState<RoomInfo | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pidRef = useRef(getOrCreatePlayerId());
  const broadcastRef = useRef<RealtimeChannel | null>(null);
  const handlerRef = useRef<((e: GameBroadcast) => void) | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const cleanup = useCallback(() => {
    stopPoll();
    if (broadcastRef.current) {
      supabase.removeChannel(broadcastRef.current);
      broadcastRef.current = null;
    }
  }, [stopPoll]);

  useEffect(() => () => cleanup(), [cleanup]);

  const send = useCallback((e: GameBroadcast) => {
    broadcastRef.current?.send({ type: 'broadcast', event: e.event, payload: e });
  }, []);

  const onEvent = useCallback((fn: (e: GameBroadcast) => void) => {
    handlerRef.current = fn;
  }, []);

  // Set up the broadcast channel for in-game communication
  const setupBroadcast = useCallback((code: string) => {
    if (broadcastRef.current) supabase.removeChannel(broadcastRef.current);
    const ch = supabase.channel(`mt_game_${code}`, {
      config: { broadcast: { self: false } },
    });
    ch.on('broadcast', { event: '*' }, ({ payload }) => {
      handlerRef.current?.(payload as GameBroadcast);
    });
    ch.subscribe((chStatus) => {
      console.log(`[OnlineRoom] Broadcast channel status: ${chStatus}`);
    });
    broadcastRef.current = ch;
  }, []);

  // ── HOST: poll DB every 1s while waiting for guest ──────────────────
  useEffect(() => {
    if (status !== 'waiting' || !room) return;

    const poll = async () => {
      try {
        const { data, error: e } = await supabase
          .from('online_rooms')
          .select()
          .eq('id', room.id)
          .maybeSingle();

        if (e) { console.error('[OnlineRoom] Poll error:', e); return; }
        if (!data) return;

        if (data.status === 'playing') {
          console.log('[OnlineRoom] Guest joined! Starting game...');
          setRoom(data as RoomInfo);
          stopPoll();
          setupBroadcast(data.room_code);
          setTimeout(() => setStatus('playing'), 800);
        }
      } catch (err) {
        console.error('[OnlineRoom] Poll exception:', err);
      }
    };

    pollRef.current = setInterval(poll, 1000);
    poll();
    return stopPoll;
  }, [status, room, stopPoll, setupBroadcast]);

  // ── Create room ────────────────────────────────────────────────────
  const createRoom = useCallback(async (name: string, gameType: ChallengeType, difficulty: Difficulty) => {
    setStatus('creating');
    setError(null);

    try {
      const code = generateCode();
      console.log(`[OnlineRoom] Creating room with code: ${code}`);

      const { data, error: err } = await supabase
        .from('online_rooms')
        .insert({
          room_code: code,
          host_id: pidRef.current,
          host_name: name,
          status: 'waiting',
          game_type: gameType,
          difficulty,
        })
        .select()
        .maybeSingle();

      if (err) {
        console.error('[OnlineRoom] Create error:', err);
        throw new Error(`Failed to create room: ${err.message}`);
      }
      if (!data) throw new Error('Failed to create room: no response');

      console.log('[OnlineRoom] Room created:', data.id, 'code:', data.room_code);
      setRoom(data as RoomInfo);
      setIsHost(true);
      setStatus('waiting');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '알 수 없는 오류';
      console.error('[OnlineRoom] createRoom failed:', msg);
      setError(msg);
      setStatus('error');
    }
  }, []);

  // ── Join room ────────────────────────────────────────────────────
  const joinRoom = useCallback(async (code: string, name: string) => {
    setStatus('joining');
    setError(null);

    const cleanCode = code.toUpperCase().trim();
    console.log(`[OnlineRoom] Joining room: ${cleanCode} as "${name}"`);

    try {
      // Step 1: Find the room
      const { data: rows, error: fe } = await supabase
        .from('online_rooms')
        .select()
        .eq('room_code', cleanCode)
        .eq('status', 'waiting')
        .limit(1);

      if (fe) {
        console.error('[OnlineRoom] Find room error:', fe);
        throw new Error(`Room lookup failed: ${fe.message}`);
      }
      if (!rows || rows.length === 0) {
        throw new Error(`Room "${cleanCode}" not found. Double-check the code.`);
      }

      const r = rows[0];
      console.log('[OnlineRoom] Found room:', r.id, '| host:', r.host_name);

      if (r.guest_id) throw new Error('This room is already full.');
      if (r.host_id === pidRef.current) throw new Error("You can't join your own room.");

      // Step 2: Join the room (update DB)
      const { data: updated, error: joinErr } = await supabase
        .from('online_rooms')
        .update({
          guest_id: pidRef.current,
          guest_name: name,
          status: 'playing',
        })
        .eq('id', r.id)
        .eq('status', 'waiting')
        .select()
        .maybeSingle();

      if (joinErr) {
        console.error('[OnlineRoom] Join update error:', joinErr);
        throw new Error(`Failed to join: ${joinErr.message}`);
      }
      if (!updated) {
        throw new Error('Join failed: someone else may have joined, or the room closed.');
      }

      console.log('[OnlineRoom] Joined successfully! Setting up channels...');
      setRoom(updated as RoomInfo);
      setIsHost(false);

      setupBroadcast(updated.room_code);
      setStatus('playing');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '알 수 없는 오류';
      console.error('[OnlineRoom] joinRoom failed:', msg);
      setError(msg);
      setStatus('error');
    }
  }, [setupBroadcast]);

  const leaveRoom = useCallback(async () => {
    if (room && isHost) {
      // If host leaves, close the room
      await supabase
        .from('online_rooms')
        .update({ status: 'finished' })
        .eq('id', room.id)
        .eq('status', 'waiting');
    }
    cleanup();
    setRoom(null);
    setStatus('idle');
    setError(null);
    setIsHost(false);
  }, [room, isHost, cleanup]);

  const finishGame = useCallback(async () => {
    if (!room) return;
    await supabase.from('online_rooms').update({ status: 'finished' }).eq('id', room.id);
  }, [room]);

  return {
    status,
    room,
    isHost,
    playerId: pidRef.current,
    error,
    createRoom,
    joinRoom,
    leaveRoom,
    send,
    onEvent,
    finishGame,
  };
}
